import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Upload, FileText, Save, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAlert } from '../../context/AlertContext';
import { useSite } from '../../context/SiteContext';
import TabNavigation from '../../components/TabNavigation';
import { formatRoleName } from '../../utils/roleUtils';

// Show "Name (Human Readable Role)" — never the technical role id.
const formatReaderLabel = (u) => {
  const role = formatRoleName(u.role);
  return role ? `${u.name} (${role})` : u.name;
};

const DocumentCreate = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning } = useAlert();
  const { activeSiteId } = useSite();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [creationMethod, setCreationMethod] = useState('editor'); // 'editor' or 'upload'
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    document_type: 'SOP',
    category: 'Safety',
    department_id: '',
    location: '',
    applicable_roles: [],
    keywords: '',
    mandatory_reading: false,
    target_readers: [],
    content: '' // For editor mode
  });
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [titleError, setTitleError] = useState('');
  const [usersError, setUsersError] = useState('');

  useEffect(() => {
    fetchUsers();
    const token = localStorage.getItem('token');
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/departments`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setDepartments(res.data.departments || []))
      .catch(() => setDepartments([]));
  }, []);

  const fetchUsers = async () => {
    setUsersError('');
    try {
      const token = localStorage.getItem('token');
      // /users/directory needs no users:read permission and returns only the
      // minimal fields a reader picker needs. Site-scoped when a site is
      // active — never fall back to an unscoped list (site isolation).
      const url = activeSiteId
        ? `${process.env.REACT_APP_BACKEND_URL}/api/users/directory?site_id=${activeSiteId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/users/directory`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsersError('Could not load the list of users. Please retry or contact your administrator.');
    }
  };

  const checkTitleExists = async (title) => {
    // `required` on the input only rejects an EMPTY field — a title of spaces
    // satisfies it, which is how blank-titled documents were being created. The
    // server rejects it too; this just says so before the round trip.
    if (!title.trim()) {
      setTitleError(title.length ? 'Title cannot be only spaces' : '');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/check-title`,
        {
          params: { title: title.trim() },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (response.data.exists) {
        setTitleError('A document with this title already exists');
      } else {
        setTitleError('');
      }
    } catch (error) {
      // Silently fail - backend will still validate on submit
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'title' && titleError) {
      setTitleError('');
    }
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file size (50MB max)
      if (selectedFile.size > 50 * 1024 * 1024) {
        showError('File size exceeds 50MB limit');
        return;
      }
      // Validate file type
      const allowedTypes = ['.pdf', '.docx', '.xlsx', '.pptx', '.jpg', '.jpeg', '.png'];
      const fileExt = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
      if (!allowedTypes.includes(fileExt)) {
        showError(`File type ${fileExt} not allowed. Allowed types: ${allowedTypes.join(', ')}`);
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleMultiSelect = (e, field) => {
    const options = Array.from(e.target.selectedOptions, option => option.value);
    setFormData({ ...formData, [field]: options });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Guard before any request: the browser's own `required` check passes on a
    // field containing only spaces, so it cannot be relied on here.
    const cleanTitle = formData.title.trim();
    if (!cleanTitle) {
      setTitleError('Title is required and cannot be only spaces');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      // Step 1: Create document metadata
      const keywordsArray = formData.keywords.split(',').map(k => k.trim()).filter(k => k);
      const docPayload = {
        ...formData,
        // Send the trimmed title, so what is stored is what the list will show.
        title: cleanTitle,
        keywords: keywordsArray,
        department_id: formData.department_id || null,
        // Scope the document to the active site; null ("All Sites") creates an
        // organisation-wide document.
        site_id: activeSiteId || null
      };

      const createResponse = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/create`,
        docPayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const docId = createResponse.data.id;

      // Step 2: Upload file or content
      if (creationMethod === 'upload' && file) {
        // Upload file
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('version_type', 'Minor');
        formDataUpload.append('change_description', 'Initial version');

        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/documents/${docId}/upload`,
          formDataUpload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(percentCompleted);
            }
          }
        );
      } else if (creationMethod === 'editor' && formData.content) {
        // Update content via editor
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/documents/${docId}/content`,
          {
            content: formData.content,
            version_type: 'Minor',
            change_description: 'Initial version'
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      showSuccess('Document created successfully!');
      navigate('/documents');
    } catch (error) {
      console.error('Error creating document:', error);
      showError(error.response?.data?.detail || 'Failed to create document');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/documents')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Documents
          </Button>

          <h1 className="text-3xl font-bold text-gray-900">Create New Document</h1>
          <p className="text-gray-600 mt-1">Create an SOP, policy, or procedure</p>
        </div>

        {/* Creation Method Selection */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">How would you like to create this document?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => setCreationMethod('editor')}
              className={`p-6 border-2 rounded-lg text-left transition-all ${
                creationMethod === 'editor'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <FileText className="w-8 h-8 text-blue-600 mb-2" />
              <h3 className="font-semibold text-gray-900">Browser Editor</h3>
              <p className="text-sm text-gray-600 mt-1">Create content directly in the browser</p>
            </button>

            <button
              onClick={() => setCreationMethod('upload')}
              className={`p-6 border-2 rounded-lg text-left transition-all ${
                creationMethod === 'upload'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <Upload className="w-8 h-8 text-blue-600 mb-2" />
              <h3 className="font-semibold text-gray-900">Upload File</h3>
              <p className="text-sm text-gray-600 mt-1">Upload an existing document file</p>
            </button>
          </div>
        </div>

        {/* Document Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900">Document Information</h2>

          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                onBlur={(e) => checkTitleExists(e.target.value)}
                required
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                  titleError ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., Lockout/Tagout Procedure"
              />
              {titleError && (
                <p className="mt-1 text-sm text-red-600">{titleError}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Type <span className="text-red-500">*</span>
              </label>
              <select
                name="document_type"
                value={formData.document_type}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="SOP">SOP</option>
                <option value="Work_Instruction">Work Instruction</option>
                <option value="Policy">Policy</option>
                <option value="Form">Form</option>
                <option value="Checklist">Checklist</option>
                <option value="Manual">Manual</option>
                <option value="SDS">SDS (Safety Data Sheet)</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Brief description of the document"
            />
          </div>

          {/* Classification */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="Safety">Safety</option>
                <option value="Operations">Operations</option>
                <option value="Quality">Quality</option>
                <option value="Environmental">Environmental</option>
                <option value="HR">HR</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Department
              </label>
              <select
                name="department_id"
                value={formData.department_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">— Select department —</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Plant A, Building 2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Keywords (comma-separated)
            </label>
            <input
              type="text"
              name="keywords"
              value={formData.keywords}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., lockout, tagout, energy isolation"
            />
          </div>

          {/* Applicable Roles */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Applicable Roles (Hold Ctrl/Cmd to select multiple)
            </label>
            <select
              multiple
              value={formData.applicable_roles}
              onChange={(e) => handleMultiSelect(e, 'applicable_roles')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 h-24"
            >
              <option value="Safety Manager">Safety Manager</option>
              <option value="Safety Officer">Safety Officer</option>
              <option value="Supervisor">Supervisor</option>
              <option value="Operator">Operator</option>
              <option value="Technician">Technician</option>
              <option value="Engineer">Engineer</option>
              <option value="Contractor">Contractor</option>
            </select>
            {formData.applicable_roles.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.applicable_roles.map(role => (
                  <span key={role} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">
                    {role}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Mandatory Reading */}
          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="mandatory_reading"
                checked={formData.mandatory_reading}
                onChange={handleInputChange}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Mandatory Reading (requires acknowledgment)
              </span>
            </label>
          </div>

          {/* Target Readers */}
          {formData.mandatory_reading && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Readers (Hold Ctrl/Cmd to select multiple) <span className="text-red-500">*</span>
              </label>
              <select
                multiple
                value={formData.target_readers}
                onChange={(e) => handleMultiSelect(e, 'target_readers')}
                required={formData.mandatory_reading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 h-32"
              >
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {formatReaderLabel(user)}
                  </option>
                ))}
              </select>
              {usersError && (
                <p className="text-sm text-red-600 mt-1">{usersError}</p>
              )}
              {!usersError && users.length === 0 && (
                <p className="text-sm text-amber-600 mt-1">
                  No eligible readers for the selected site. Assign users to this site under Admin Management before creating a mandatory-reading document.
                </p>
              )}
              {formData.target_readers.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm text-gray-600">{formData.target_readers.length} user(s) selected</p>
                </div>
              )}
            </div>
          )}

          {/* Content / File Upload */}
          {creationMethod === 'editor' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Content
              </label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                rows={15}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder="Write your document content here...\n\nYou can use this editor to create procedures, instructions, and policies."
              />
              <p className="text-sm text-gray-500 mt-1">
                Tip: You can paste content from Word, Google Docs, or other sources.
              </p>
            </div>
          )}

          {creationMethod === 'upload' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Document File
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.docx,.xlsx,.pptx,.jpg,.jpeg,.png"
                />
                <label
                  htmlFor="file-upload"
                  className="flex flex-col items-center justify-center cursor-pointer"
                >
                  <Upload className="w-12 h-12 text-gray-400 mb-2" />
                  <p className="text-gray-600 text-sm mb-1">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-gray-500 text-xs">
                    PDF, DOCX, XLSX, PPTX, JPG, PNG (max 50MB)
                  </p>
                </label>
                {file && (
                  <div className="mt-4 text-center">
                    <p className="text-sm text-gray-700 font-medium">{file.name}</p>
                    <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                    <button
                      type="button"
                      onClick={() => {
                        setFile(null);
                        setUploadProgress(0);
                        // Reset file input
                        const fileInput = window.document.getElementById('file-upload');
                        if (fileInput) fileInput.value = '';
                      }}
                      className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                    >
                      Remove file
                    </button>
                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex items-center justify-end space-x-4 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/documents')}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || titleError || !formData.title.trim() || (creationMethod === 'upload' && !file) || (creationMethod === 'editor' && !formData.content)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Create Document
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
    </div>
  );
};

export default DocumentCreate;
